/* File: frontend/src/pages/UserProfileAdmin.js */
/* Version: 2.1 (Task T3 Screen 8 — Full i18n toasts/confirm, role string parsing, and gap alignment) */
/* Date: 2026-10-20 */
/* Description: 
   - [UI]: Fully converted to Design System primitives (PageHeader, Card, Badge, Button).
   - [I18N]: Comprehensive bilingual EN/RU support via useUiLanguage hook and practice translation keys
             including toasts, confirmation dialogs, and error messages.
   - [LOGIC]: Preserved Google Calendar connection and persona update handlers.
   - [BEHAVIOR]: Robust user role display handling both object {name} and primitive string shapes.
   - [ACCESSIBILITY]: Semantic CardHeader / CardTitle structure and native Button semantics.
*/

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import { paralegalSkryt } from '../utils/paralegalScreen';
import { User, Mail, Calendar, Shield, Activity, CheckCircle, Bot } from 'lucide-react';
import {
    PageHeader,
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    Badge,
    Button
} from '../components/ui';
import { Sostoyanie } from '../components/FitenkoUzor';
import './UserProfileAdmin.css';

const UserProfileAdmin = () => {
    const { user, loading: authLoading, updateUser } = useAuth();
    const { t } = useUiLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const handledAuthSearchRef = useRef(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [aiPersona, setAiPersona] = useState(user?.ai_persona || 'colleague');

    // Update state when user object changes
    useEffect(() => {
        if (user && user.ai_persona) {
            setAiPersona(user.ai_persona);
        }
    }, [user]);

    // Handle return from Google OAuth
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const authStatus = queryParams.get('google_auth');
        if (!authStatus || handledAuthSearchRef.current === location.search) {
            return;
        }
        handledAuthSearchRef.current = location.search;

        const detail = queryParams.get('detail');

        if (authStatus === 'success') {
            toast.success(t('practice.profile.toast_google_success'));
            window.history.replaceState({}, document.title, location.pathname);
            window.location.reload();
        } else if (authStatus === 'error') {
            toast.error(t('practice.profile.toast_google_error', { detail: detail || t('practice.profile.unknown_error') }));
            window.history.replaceState({}, document.title, location.pathname);
            navigate(location.pathname, { replace: true });
        }
    }, [location.pathname, location.search, navigate, t]);

    // Google Calendar connect handler
    const handleConnectGoogleCalendar = async () => {
        setIsConnecting(true);
        const toastId = toast.loading(t('practice.profile.toast_redirecting_google'));
        try {
            const response = await apiService.getGoogleAuthUrl();
            if (response && response.authorization_url) {
                window.location.href = response.authorization_url;
            } else {
                throw new Error(t('practice.profile.error_no_auth_url'));
            }
        } catch (error) {
            toast.error(t('practice.profile.toast_error', { error: error.message }), { id: toastId });
            setIsConnecting(false);
        }
    };

    // Google Calendar disconnect handler
    const handleDisconnect = async () => {
        if (!window.confirm(t('practice.profile.confirm_disconnect_google'))) return;
        
        setIsDisconnecting(true);
        const toastId = toast.loading(t('practice.profile.toast_disconnecting'));
        try {
            const updatedUser = await apiService.disconnectGoogleCalendar();
            updateUser(updatedUser); 
            toast.success(t('practice.profile.toast_disconnect_success'), { id: toastId });
        } catch (error) {
            toast.error(t('practice.profile.toast_failed', { error: error.message }), { id: toastId });
        } finally {
            setIsDisconnecting(false);
        }
    };

    // AI persona update handler
    const handlePersonaChange = async (newPersona) => {
        setAiPersona(newPersona);
        const toastId = toast.loading(t('practice.profile.toast_updating_persona'));
        try {
            const updatedUser = await apiService.updateMyPersona(newPersona);
            updateUser(updatedUser); 
            toast.success(t('practice.profile.toast_persona_updated'), { id: toastId });
        } catch (error) {
            toast.error(t('practice.profile.toast_failed', { error: error.message }), { id: toastId });
            if (user && user.ai_persona) {
                setAiPersona(user.ai_persona);
            }
        }
    };

    const { features } = useBranding();
    const isParalegalHidden = paralegalSkryt(features);

    if (authLoading) return <div className="loading-state"><Sostoyanie tekst={t('practice.profile.loading')} /></div>;
    if (!user) return <div className="error-state"><Sostoyanie tekst={t('practice.profile.not_found')} /></div>;

    const isIntegrated = user.google_calendar_integrated;

    return (
        <div className="fk-page user-profile-admin-container bg-background text-foreground">
            {/* Header Section */}
            <PageHeader
                kicker={t('practice.profile.kicker')}
                title={t('practice.profile.title')}
                subtitle={t('practice.profile.subtitle')}
                badge={
                    <Badge variant={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? t('practice.profile.active') : t('practice.profile.inactive')}
                    </Badge>
                }
            />

            {/* Content Grid */}
            <div className="profile-grid">

                {/* CARD 1: User Details */}
                <Card className="details-card">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                        <User size={20} style={{ color: 'var(--accent-ink)', flexShrink: 0 }} />
                        <CardTitle style={{ margin: 0 }}>{t('practice.profile.personal_info')}</CardTitle>
                    </CardHeader>
                    <CardContent style={{ padding: '1.5rem' }}>
                        <div className="info-row">
                            <label><User size={16}/> {t('practice.profile.full_name')}</label>
                            <span className="info-value">{user.first_name} {user.last_name}</span>
                        </div>
                        <div className="info-row">
                            <label><Mail size={16}/> {t('practice.profile.email')}</label>
                            <span className="info-value">{user.email}</span>
                        </div>
                        <div className="info-row">
                            <label><Shield size={16}/> {t('practice.profile.role')}</label>
                            <span className="role-pill">
                                {user.roles?.[0]?.name?.replace('_', ' ') || (typeof user.roles?.[0] === 'string' ? user.roles[0].replace('_', ' ') : t('practice.profile.role_user'))}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {/* CARD 2: Integrations */}
                <Card className="integrations-card">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                        <Activity size={20} style={{ color: 'var(--accent-ink)', flexShrink: 0 }} />
                        <CardTitle style={{ margin: 0 }}>{t('practice.profile.integrations')}</CardTitle>
                    </CardHeader>
                    <CardContent style={{ padding: '1.5rem' }}>
                        <div className="integration-item">
                            <div className="integration-left">
                                <div className="integration-icon-bg">
                                    <Calendar size={24} color="currentColor" />
                                </div>
                                <div className="integration-info">
                                    <h3>{t('practice.profile.google_calendar')}</h3>
                                    <p>{isParalegalHidden ? t('practice.profile.sync_calendar_general') : t('practice.profile.sync_calendar_paralegal')}</p>
                                </div>
                            </div>

                            <div className="integration-right">
                                {isIntegrated ? (
                                    <Button
                                        variant="outline"
                                        style={{ color: 'var(--danger)', borderColor: 'var(--border)' }}
                                        onClick={handleDisconnect}
                                        disabled={isDisconnecting}
                                    >
                                        {isDisconnecting ? t('practice.profile.disconnecting') : t('practice.profile.disconnect')}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="default"
                                        onClick={handleConnectGoogleCalendar}
                                        disabled={isConnecting}
                                    >
                                        {isConnecting ? t('practice.profile.connecting') : t('practice.profile.connect')}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {isIntegrated && (
                            <div className="integration-status-bar">
                                <CheckCircle size={16} /> {t('practice.profile.connected_as', { email: user.email })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* CARD 3: AI Customization */}
                {user?.email === 'afitbk@gmail.com' && (
                    <Card className="ai-customization-card" style={{ gridColumn: '1 / -1' }}>
                        <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                            <Bot size={20} style={{ color: 'var(--accent-ink)', flexShrink: 0 }} />
                            <CardTitle style={{ margin: 0 }}>{t('practice.profile.ai_persona_title')}</CardTitle>
                        </CardHeader>
                        <CardContent style={{ padding: '1.5rem' }}>
                            <div className="info-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <p className="text-muted-foreground" style={{ fontSize: '0.9rem', margin: 0 }}>
                                    {t('practice.profile.ai_persona_desc')}
                                </p>
                                <select
                                    value={aiPersona}
                                    onChange={(e) => handlePersonaChange(e.target.value)}
                                    className="bg-popover text-foreground border border-input"
                                    style={{
                                        padding: '10px',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: '1rem',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '400px'
                                    }}
                                >
                                    <option value="colleague">{t('practice.profile.persona_colleague')}</option>
                                    <option value="friend">{t('practice.profile.persona_friend')}</option>
                                    <option value="close_friend">{t('practice.profile.persona_close_friend')}</option>
                                    <option value="romantic">{t('practice.profile.persona_romantic')}</option>
                                </select>
                            </div>
                        </CardContent>
                    </Card>
                )}

            </div>
        </div>
    );
};

export default UserProfileAdmin;
